import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { OAuthService } from 'angular-oauth2-oidc';
import { environment } from '../../../../environments/environment';
import { Observable, map, of } from 'rxjs';
import { CreateTokenOutcome, SsoRedemptionResponse, TokenDTO, TokensRequest, TokensResponse } from '@brain-auth/models/auth-token-dto.model';
import { STORAGE } from 'constants/storage.constant';
import { jwtDecode } from 'jwt-decode';
import { Router } from '@angular/router';
import { getSignInUrl, getSsoCallbackUrl } from 'shared/services/utils/url/url.service';

/** BRN-2148: this webapp's fixed identity when talking to brain-iam's SSO endpoints (a compile-time
 *  product label, not a per-environment value — never sourced from an env var). */
const SSO_PRODUCT_ID = 'brain';

/** BRN-2208: the enrichment claim this app reads. One of the five `https://gridwatchems.com/claims/*`
 *  keys the Auth0 post-login Action writes (contract: brain-iam `auth0/.../CLAIM-CONTRACT.md`). */
export const ROLES_CLAIM = 'https://gridwatchems.com/claims/roles';

/**
 * BRN-2208 (D5): what the browser can say about the current session.
 *
 * - `no-session` — nobody is signed in (no identity claims stored at all).
 * - `unenriched` — somebody IS signed in with a structurally valid token that was never enriched
 *   with the BRAIN claims (or whose roles claim is malformed). Signing out and back in re-issues the
 *   same token, so treating this as `no-session` is what produced the silent re-login loop.
 * - `authenticated` — the session carries a well-formed roles claim.
 */
export type SessionState = 'no-session' | 'unenriched' | 'authenticated';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  constructor(
    private http: HttpClient,
    private oAuthService: OAuthService,
    private router: Router,
  ) {}

  login(email: string, password: string) {
    const body = {
      email,
      password,
    };

    return this.http.post<TokenDTO>(`${environment.baseIamUrl}/api/public/v1/auth/oauth-tokens`, body);
  }

  loginMfa(tokenDto: TokenDTO) {
    const body = {
      mfaToken: tokenDto.mfaToken,
      oobCode: tokenDto.oobCode,
      bindingCode: tokenDto.bindingCode,
    };

    return this.http.post<TokenDTO>(`${environment.baseIamUrl}/api/public/v1/auth/mfa-tokens`, body);
  }

  askForPasswordChange(email: string) {
    const body = {
      email,
    };

    return this.http.post<string>(`${environment.baseIamUrl}/api/public/v1/auth/password-resets`, body);
  }

  postTokens(payload: TokensRequest): Observable<TokensResponse> {
    return this.http.post<TokensResponse>(`${environment.baseIamUrl}/api/public/v1/tokens`, payload);
  }

  /** BRN-2148 (operator-set 2026-07-29): full-page navigation target for an SSO provider button
   *  (SP-initiated, IAM-driven redirect — no client-side OIDC library, per arch-review.md).
   *  Carries NO email: the login screen no longer asks for one before the IdP round-trip, and the
   *  identity is only known after it. `return_uri` must match brain-iam's per-product registered
   *  allowlist entry; this webapp sends the real browser origin it is running under (this product's
   *  tenant-subdomain model — see `TenantService`/`deriveBaseIamUrl` — means that origin varies per
   *  tenant; flagged as a cross-shard gap in implementation-notes-shard-D2.md). */
  ssoAuthorizeUrl(provider: 'google' | 'okta'): string {
    const returnUri = `${window.location.origin}${getSsoCallbackUrl()}`;
    const params = new URLSearchParams({ product: SSO_PRODUCT_ID, return_uri: returnUri });
    return `${environment.baseIamUrl}/api/public/v1/sso/${provider}/authorize?${params.toString()}`;
  }

  /** BRN-2148: redeems the one-time SSO handoff `code` (never a token) for the session bundle, via
   *  the NEW POST endpoint (never the existing GET .../tokens/{id} route — that would leak the code
   *  into logs/history for this leg). Reshapes the response into the same `TokenDTO` shape
   *  `saveStorageAndLoadUserProfile` already knows, so the SSO session is established through the
   *  IDENTICAL storage code path a password login uses (AC1 parity). brain-iam's redemption response
   *  carries no separate `id_token` (see `SsoRedemptionResponse`'s doc comment) — the single JWT
   *  serves both roles, mirroring brain-iam's own internal modeling. */
  redeemSsoHandoff(code: string): Observable<TokenDTO> {
    return this.http
      .post<SsoRedemptionResponse>(`${environment.baseIamUrl}/api/public/v1/tokens/sso-redemption`, { code })
      .pipe(
        map(
          (response): TokenDTO => ({
            outcome: CreateTokenOutcome.SUCCESS,
            accessToken: response.access_token,
            idToken: response.access_token,
            refreshToken: response.refresh_token,
          }),
        ),
      );
  }

  saveStorageAndLoadUserProfile(tokenDto: TokenDTO) {
    localStorage.setItem(STORAGE.accessToken, tokenDto.accessToken ?? '');

    let idTokenClaims = null;

    if (tokenDto.idToken) {
      try {
        idTokenClaims = JSON.stringify(jwtDecode(tokenDto.idToken));
        localStorage.setItem(STORAGE.idTokenClaimsObj, idTokenClaims);
        localStorage.setItem(STORAGE.idToken, tokenDto.idToken);
      } catch (error) {
        console.error('Failed to decode idToken:', error);
      }
    }

    return of(idTokenClaims);
  }

  logOut() {
    this.oAuthService.logOut();
    void this.router.navigate([getSignInUrl()]);
  }

  userAuthenticated() {
    const claims = this.identityClaims();
    const roles = claims && this.readEnrichedRoles(claims);

    if (claims && roles) {
      return {
        ...claims,
        roles,
      };
    }
    return null;
  }

  /**
   * BRN-2208 (D5 / AC7): tells an unenriched session apart from no session at all — the distinction
   * `userAuthenticated()`'s null-collapse erases, and the reason an unenriched sign-in looked to the
   * app exactly like a signed-out one and was bounced back to the login screen forever.
   *
   * **This answer is a HINT, never the authority (L3 SEC-11).** It reads the ID token's claims,
   * while every backend reads the ACCESS token's; an Auth0 Action sets those independently
   * (`api.idToken.setCustomClaim` vs `api.accessToken.setCustomClaim`), so the two can disagree in
   * both directions. The authoritative signal is a backend's `unenriched_token` 401, handled in
   * `HttpErrorInterceptor` — no guard or route may hard-fail a session on this read alone.
   */
  sessionState(): SessionState {
    const claims = this.identityClaims();

    if (!claims) {
      return 'no-session';
    }

    return this.readEnrichedRoles(claims) ? 'authenticated' : 'unenriched';
  }

  private identityClaims(): Record<string, unknown> | null {
    return (this.oAuthService.getIdentityClaims() as Record<string, unknown> | null) ?? null;
  }

  /**
   * Element-typed, not just `is-an-array` (L3 SEC-11, the frontend mirror of the backend's SEC-4
   * guard): `claims[ROLES_CLAIM] as string[]` is an ERASED cast, so a present-but-malformed claim
   * (a bare string, an object, a list holding a number) used to reach `.map(r => r.toUpperCase())`
   * and throw a TypeError out of the NgRx effect instead of being classified as unenriched.
   *
   * An ABSENT claim and a MALFORMED one both mean "not enriched"; an empty list stays enriched,
   * exactly as before — narrowing that would change behaviour beyond this failure path.
   */
  private readEnrichedRoles(claims: Record<string, unknown>): string[] | null {
    const roles = claims[ROLES_CLAIM];

    if (!Array.isArray(roles) || !roles.every(role => typeof role === 'string')) {
      return null;
    }

    return (roles as string[]).map(role => role.toUpperCase());
  }

  getUserEmail(): string | undefined {
    const emailClaim = 'https://gridwatchems.com/claims/email';
    const userAuthenticated = this.userAuthenticated();
    return ((userAuthenticated as any)?.[emailClaim] as string) || undefined;
  }

  requestOtpChallenge() {
    return this.http.post<void>(`${environment.baseIamUrl}/api/user/v1/otp/challenge`, {});
  }

  verifyOtpChallenge(email: string, code: string, facilityGroupId: number) {
    return this.http.post(`${environment.apiBaseUrl}/user/otp/verification`, {
      email,
      code,
      facilityGroupId,
    });
  }
}
